import { hostTargetOrigin } from '../utils/embedOrigins';

const _ghostAnimalEmojis = ['🐻', '🦀', '🐊', '🐶', '🐬', '🐟', '🐸', '🐹', '🐨', '🦁', '🐙', '🐼', '🐑', '🦃', '🐢', '🦄'];

export function convertKeysToCamelCase(obj) {
  if (Array.isArray(obj))
    return obj.map(item => convertKeysToCamelCase(item));
  if (obj && typeof obj === 'object') {
    const newObj = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
        newObj[camelKey] = convertKeysToCamelCase(obj[key]);
      }
    }
    return newObj;
  }
  return obj;
}

// Returns a deep copy of `value` with every token-bearing key (jwt, token, userToken,
// accessToken, id_token, authorization, AuthCode — case-insensitive, at any depth, incl. inside arrays)
// replaced by '<redacted>', without mutating the original. Non-token keys are copied through
// untouched. Used to keep host JWTs and 7-day userTokens out of console/debug logs.
export function redactSensitiveFields(value) {
  if (Array.isArray(value))
    return value.map(item => redactSensitiveFields(item));
  if (value && typeof value === 'object') {
    const newObj = {};
    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        newObj[key] = /jwt|token|authorization|authcode/i.test(key) ? '<redacted>' : redactSensitiveFields(value[key]);
      }
    }
    return newObj;
  }
  return value;
}

import { useMainStore } from '@store/main';
// Plugin helpers run outside component context, so there is no this.$t(). They resolve keys through
// i18n.global.t() instead — the same pattern the store's actions use for WS-CONNECTION-ERROR.
import i18n from '../i18n';

export default {
  install(app, options) {
    //Vue Global Scope Properties: prepend with $FUS
    app.config.globalProperties.$FUSauctionProId = 'oenlbmjmflpfjmijdnhojkhjkgpbjpfk';
    app.config.globalProperties.$FUSmaxIntValue = 2147483647;
    app.config.globalProperties.$FUSminIntValue = -2147483647;
    app.config.globalProperties.$FUSsystemUserGuid = '54f712e5-eba3-498d-ac8e-acd8d488a191';
    app.config.globalProperties.$FUSdealerWebsiteGuid = 'daf9a586-5d69-405e-8ac0-6ab94332d7d9';
    app.config.globalProperties.$FUSsystemUserPrefix = 'system_';
    // A getter, not a plain string: a value assigned here would be resolved once at plugin-install
    // time and freeze the language at app startup, never following a later switch. Defined this way
    // the template reads it during render, t() runs fresh, and the render effect tracks the active
    // locale exactly like any $t() binding. Kept as a $FUS* property so the four call sites
    // (FusionMain, FusionToolbar, FusionBody, FusionController) don't have to change.
    Object.defineProperty(app.config.globalProperties, '$FUSunauthorizedMessage', {
      get() { return i18n.global.t('common.unauthorized'); },
      enumerable: true,
      configurable: true,
    });
    app.config.globalProperties.$FUSeraUserGuids = ['3f2fc7b8-0f4d-4143-86ad-09803d750ed0', '393ec934-04b3-4370-9a3f-e2c0b9131968', '305a4170-27bd-4b58-8316-6aff982d6496', '3fe3318a-2cce-4db7-908d-aef1f6b0a95c'];
    app.config.globalProperties.$FUSfocusUserGuids = ['4715ea82-90d1-46c9-9da7-a3cb099c44cb', 'bf62b7eb-c812-4261-93a8-42233552b07c', 'f8559e3f-14fb-4ce9-a11d-b1cad89a2afe'];
    app.config.globalProperties.$FUSfusionAvatarColorsHex = ['#49A4E3', '#DE524C', '#95C942', '#EAA13B', '#4C83EE', '#5EC169', '#C956E7', '#E97A35', '#636AE9', '#51B4D0', '#8362EE', '#55B685', '#E3B43E', '#54B5A6', '#9D5EEF', '#54B5A6'];
    app.config.globalProperties.$FUSfusionChartColorsHex = ['#49A4E3', '#E97A35', '#95C942', '#9D5EEF', '#E3B43E', '#51B4D0', '#DF6464', '#C956E7', '#EAA13B', '#4C83EE', '#64DFDD', '#55B685'];
    app.config.globalProperties.$FUSfusionAvatarFirstNames = ['Cheerful', 'Curious', 'Zesty', 'Peppy', 'Bubbly', 'Witty', 'Jolly', 'Ornery', 'Daring', 'Sunny', 'Lively', 'Chipper', 'Peachy', 'Zippy', 'Snazzy', 'Nifty', 'Quirky', 'Merry', 'Radiant', 'Jazzy', 'Nimble', 'Chirpy'];
    app.config.globalProperties.$FUSfusionAvatarLastNames = ['Bear', 'Crab', 'Crocodile', 'Dog', 'Dolphin', 'Fish', 'Frog', 'Hamster', 'Koala', 'Lion', 'Octopus', 'Panda', 'Sheep', 'Turkey', 'Turtle', 'Unicorn'];

    //Vue Global Scope Methods: prepend with $FUS
    // NOTE: the generated adjective+animal names below stay ENGLISH on purpose. They are a stable
    // pseudonymous identity hashed from the guid, so localizing them would show the same person as
    // "Cheerful Bear" to one participant and "Oso Alegre" to another in the SAME thread. Spanish and
    // French also put the adjective after the noun, so it would need per-locale ordering, not just a
    // translated word list. Only the no-guid fallback is localized.
    app.config.globalProperties.$FUSresolveGhostUser = function (guid) {
      if (!guid) return { friendlyName: i18n.global.t('common.unknownUser'), emoji: null, isGhost: true };
      const firstNames = app.config.globalProperties.$FUSfusionAvatarFirstNames;
      const lastNames = app.config.globalProperties.$FUSfusionAvatarLastNames;
      let sumEven = 0, sumOdd = 0;
      for (let i = 0; i < guid.length; i++) {
        const code = guid.charCodeAt(i);
        if (i % 2 === 0) sumEven += code;
        else sumOdd += code;
      }
      const adjectiveIdx = sumEven % firstNames.length;
      const animalIdx = sumOdd % lastNames.length;
      return {
        friendlyName: `${firstNames[adjectiveIdx]} ${lastNames[animalIdx]}`,
        emoji: _ghostAnimalEmojis[animalIdx],
        isGhost: true
      };
    };
    // Resolve an enterprise user's display name, preferring the full "First Last" (fullName) so
    // people who share a first name can be told apart, falling back to friendlyName (first name)
    // then empty. Ghost users are resolved separately via $FUSresolveGhostUser.
    app.config.globalProperties.$FUSuserDisplayName = function (user) {
      if (!user) return '';
      return user.fullName || user.friendlyName || '';
    };
    // Assemble a group-DM style label from a list of participant display names:
    //   0 names  -> ""
    //   1 name   -> "Alice Adams"
    //   2 names  -> "Alice Adams and Bob Smith"
    //   3+ names -> "Alice Adams and 2 more"   (the first name, then a count of everyone else)
    // The joining words come from the catalog so the label follows the UI language. No plural form
    // on andMore: that branch only runs at 3+ names, so the count is always 2 or more.
    app.config.globalProperties.$FUSformatParticipantNames = function (names) {
      const list = (names || []).filter(Boolean);
      if (list.length === 0) return '';
      if (list.length === 1) return list[0];
      if (list.length === 2) return i18n.global.t('common.participants.twoNames', { first: list[0], second: list[1] });
      return i18n.global.t('common.participants.andMore', { first: list[0], count: list.length - 1 });
    };
    app.config.globalProperties.$FUSparseAVImageString = function (imageString, getSource = false) {
      let avImage = { type: null, label: null, image: null };
      if (typeof imageString === 'string' && imageString.length > 0) {
        if (imageString.indexOf('::image::') >= 0) {
          avImage.type = imageString.replace(/::.*/i, '');
          avImage.image = imageString.replace(/.*::image::/i, '');
          avImage.image = avImage.image && avImage.image.indexOf('http') >= 0 ? avImage.image : `https://${avImage.image}`;
          if (avImage.type === 'ziggeo') {
            avImage.ziggeo = imageString.replace(/.*ziggeo::video::/i, '');
            avImage.ziggeo = avImage.ziggeo.replace(/::image.*/i, '');
          }
          if (imageString.indexOf('::label::') >= 0)
            avImage.label = imageString.replace(/.*::label::(.*)::image::.*/i, '$1');
        }
        else
          avImage.image = imageString;

        if (avImage.image)
          return getSource ? avImage.image : avImage;
      }

      return null;
    };
    app.config.globalProperties.$FUSsortObject = function (obj, asc = true, sortType = 'STRING') {
      if (obj && Object.keys(obj).length > 0) {
        let sortedObj = {};
        let fieldKeys = Object.keys(obj);
        fieldKeys.sort(function (a, b) {
          switch (sortType) {
            case 'STRING':
              let valA = obj[a] ? obj[a].toLowerCase() : '';
              let valB = obj[b] ? obj[b].toLowerCase() : '';
              if (asc)
                return (valA).localeCompare(valB);
              else
                return (valB).localeCompare(valA);
            case 'NUMBER':
              if (asc)
                return (obj[a] - obj[b]);
              else
                return (obj[b] - obj[a]);
          }

          return 0;
        });
        fieldKeys.forEach(fieldKey => {
          sortedObj[fieldKey] = obj[fieldKey];
        });

        return sortedObj;
      }

      return obj;
    };
    app.config.globalProperties.$FUSglobalLogging = function (message) {
      console.log(message);
    };
    app.config.globalProperties.$FUSlog = function (message, type = 'log') {
      useMainStore().captureLog(message, type);
    };
    app.config.globalProperties.$FUSarrayHasEntries = function (arr) {
      return Array.isArray(arr) && arr.length > 0;
    };
    app.config.globalProperties.$FUSobjectHasFields = function (obj) {
      return obj && Object.keys(obj).length > 0;
    };
    app.config.globalProperties.$FUSstringHasValue = function (value) {
      //returns true if value has any characters
      return Boolean(typeof value !== 'undefined' && value !== null && value !== '');
    };
    app.config.globalProperties.$FUSisValidGuid = function (guid = null) {
      //returns true if guid is a valid format
      if (!guid) return false;
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return guidRegex.test(guid);
    };
    app.config.globalProperties.$FUSisValidVin = function (vin = null) {
      // Replicates C# IsValidVin() - validates VIN using check digit algorithm
      if (!vin) return false;

      const validVinLength = 17;
      const validTaxonomyVinLength = 11;
      const checkDigitIndex = 8;

      if (vin.length !== validVinLength && vin.length !== validTaxonomyVinLength) {
        return false;
      }

      // Character transliteration values (I, O, Q are excluded from valid VINs)
      const characterTransliteration = {
        'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8,
        'J': 1, 'K': 2, 'L': 3, 'M': 4, 'N': 5, 'P': 7, 'R': 9,
        'S': 2, 'T': 3, 'U': 4, 'V': 5, 'W': 6, 'X': 7, 'Y': 8, 'Z': 9,
        '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '0': 0
      };

      // Valid check characters and their values
      const validCheckCharacters = {
        '0': 0, '1': 1, '2': 2, '3': 3, '4': 4,
        '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, 'X': 10
      };

      // Character weights for 17 characters in VIN
      const characterWeights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

      const vinUpper = vin.toUpperCase();
      const checkCharacter = vinUpper[checkDigitIndex];

      if (!validCheckCharacters.hasOwnProperty(checkCharacter)) {
        return false;
      }

      let value = 0;
      for (let i = 0; i < vinUpper.length; i++) {
        if (!characterTransliteration.hasOwnProperty(vinUpper[i])) {
          return false;
        }
        value += characterWeights[i] * characterTransliteration[vinUpper[i]];
      }

      return (value % 11) === validCheckCharacters[checkCharacter];
    };
    app.config.globalProperties.$FUSjsonCopy = function (obj) {
      return JSON.parse(JSON.stringify(obj));
    };
    app.config.globalProperties.$FUSarrayOfObjectsKeyMap = function (arrayOfObjects, byField = '') {
      let arrayOfObjectsKeyMapObject = null;

      if (Array.isArray(arrayOfObjects) && arrayOfObjects.length > 0 && byField) {
        arrayOfObjectsKeyMapObject = {};
        arrayOfObjects.forEach(x => {
          const key = x.hasOwnProperty(byField) ? x[byField] : null;
          if (key) {
            arrayOfObjectsKeyMapObject[key] = x;
          }
        });
      }
      else if (arrayOfObjects && Object.keys(arrayOfObjects).length > 0 && byField) {
        arrayOfObjectsKeyMapObject = {};
        Object.keys(arrayOfObjects).forEach(x => {
          const obj = arrayOfObjects[x];
          const val = obj && obj.hasOwnProperty(byField) ? obj[byField] : null;
          if (val) {
            arrayOfObjectsKeyMapObject[x] = val;
          }
        });
      }

      return arrayOfObjectsKeyMapObject;
    };
    app.config.globalProperties.$FUSgenerateDateNumber = function () {
      return Date.now();
    };
    app.config.globalProperties.$FUSdateFormat = function (format, dateInput) {
      let date;

      // Handle different input types
      if (typeof dateInput === 'number') {
        // Assume Unix timestamp (seconds) - convert to milliseconds
        date = new Date(dateInput * 1000);
      }
      else if (dateInput instanceof Date)
        date = dateInput;
      else if (typeof dateInput === 'string')
        date = new Date(dateInput);
      else
        return '';

      if (isNaN(date)) {
        return '';
      }

      const tokens = {
        'YYYY': date.getFullYear(),
        'YY': String(date.getFullYear()).slice(-2),
        'MM': String(date.getMonth() + 1).padStart(2, '0'),
        'M': date.getMonth() + 1,
        'DD': String(date.getDate()).padStart(2, '0'),
        'D': date.getDate(),
        'HH': String(date.getHours()).padStart(2, '0'),
        'H': date.getHours(),
        'hh': String(date.getHours() % 12 || 12).padStart(2, '0'),
        'h': date.getHours() % 12 || 12,
        'mm': String(date.getMinutes()).padStart(2, '0'),
        'm': date.getMinutes(),
        'ss': String(date.getSeconds()).padStart(2, '0'),
        's': date.getSeconds(),
        'A': date.getHours() >= 12 ? i18n.global.t('common.dateFormat.pmUpper') : i18n.global.t('common.dateFormat.amUpper'),
        'a': date.getHours() >= 12 ? i18n.global.t('common.dateFormat.pmLower') : i18n.global.t('common.dateFormat.amLower')
      };

      // Single pass over the whole format string: sequential replace() calls would re-scan the
      // SUBSTITUTED text, so a translated token value containing a shorter token's letters (e.g.
      // an am/pm word with an 'a' in it) would get substituted again. One regex matching every
      // token at once makes each replacement final.
      const tokenPattern = new RegExp(Object.keys(tokens).sort((a, b) => b.length - a.length)
        .map(token => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|'), 'g');
      return format.replace(tokenPattern, token => String(tokens[token]));
    };
    app.config.globalProperties.$FUSloadScript = function (src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = (error) => reject(error);
        document.head.appendChild(script);
      });
    };
    app.config.globalProperties.$FUSloadCSS = function (href) {
      return new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.onload = () => resolve();
        link.onerror = (error) => reject(error);
        document.head.appendChild(link);
      });
    };
    app.config.globalProperties.$FUSconvertKeysToCamelCase = function (obj) {
      return convertKeysToCamelCase(obj);
    };
    app.config.globalProperties.$FUSredactSensitiveFields = function (value) {
      return redactSensitiveFields(value);
    };
    app.config.globalProperties.$FUSisBase64 = function (str) {
      try {
        // Check if the string matches Base64 regex
        const base64Regex = /^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)?$/;
        return base64Regex.test(str) && atob(str) !== "";
      } catch (e) {
        return false;
      }
    };
    app.config.globalProperties.$FUSdecodeIfBase64 = function (str) {
      if (this.$FUSisBase64(str)) {
        // Use TextDecoder to correctly handle UTF-8 multi-byte characters (e.g. accented chars).
        // atob() alone treats output as Latin-1, mangling bytes like 0xC3 0xA9 (é) into 'Ã©'.
        const binary = atob(str);
        const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
        return new TextDecoder('utf-8').decode(bytes);
      }
      return str; // Return as-is if not Base64
    };
    app.config.globalProperties.$FUSdebounce = function (func, wait, immediate) {
      let timeout;
      return function () {
        const context = this, args = arguments;
        const later = function () {
          timeout = null;
          if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    };
    app.config.globalProperties.$FUSremoveHashParam = function (key) {
      if (typeof key !== "undefined" && key !== null && window.location.hash) {
        // Remove the leading '#' to parse the parameters
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);

        if (params.has(key)) {
          params.delete(key);
          const newHash = params.toString();
          // Update the hash: if empty, remove the '#' as well, otherwise set the new hash
          if (newHash) {
            window.location.hash = '#' + newHash;
          } else {
            // Removing the hash without causing a page reload
            history.replaceState(null, document.title, window.location.pathname + window.location.search);
          }
        }
      }
    };
    app.config.globalProperties.$FUSgetHashParam = function (key) {
      if (window.location.hash) {
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        return params.get(key);
      }
      return null;
    };
    // FUS-474 (Security Backlog M2): messages carry MCP entitlements and message previews, so they go to
    // the parent's exact origin, and only when it is an allowed host (utils/embedOrigins). They used to go
    // to '*'. The payload is no longer written to the console either.
    app.config.globalProperties.$FUSpostFusionMessage = function (messageType, messageData = null, source = 'FUSION-WEB', targetOrigin = null) {
      if (window.parent) {
        const resolvedOrigin = targetOrigin || hostTargetOrigin('parent');
        if (!resolvedOrigin)
          return;
        const postMessageData = {
          type: messageType,
          data: messageData,
          source: source
        };
        console.log(`$FUSpostFusionMessage: ${messageType} → ${resolvedOrigin}`);
        window.parent.postMessage(postMessageData, resolvedOrigin);
      }
      else
        console.warn(`$FUSpostFusionMessage: No parent window available - ${messageType}`);
    };
  },
};
